import {
  appendCountryLanguage,
  updateClassNames,
  isUserLoggedIn,
  updateCartCount,
  initializeLightbox,
  addHoverForNav,
  removeAllHoverForNav,
  toggleHoverForNav,
  removeHoverForNav,
  toggleOverflowForNav,
  injectOfficeClosureMessage,
  switchToCheckoutHeader,
  listenForImportantMessage,
  trackClick,
  loadScript,
  injectURWFont,
  addEmailSignupLinkClickListeners,
  getCountry,
  getLanguage,
  buildHeadMetaItems,
  hideCookieConsent,
  isJacketFinderPage,
  disableAdobeLaunch,
  injectWCAGStatement,
  injectPromobarMessage,
  toggleRegularPage,
  hideElementInIframe,
  hideInvalidElementsInWebView,
  isWebview,
  hideEmailSignupPopup,
  isPDPPage,
  getOneTrustLang,
} from "./GeneralHelpers";
import { toggleHotjar } from "./HotjarHelpers";
import Auth from "./Authentication";
import headerTemplateHtml from "../templates/header-outdoor";
import ArcWebSettings from "./Arcteryx";
import AdobeTargetPreScript from "./AdobeTarget";
import { setNavigationLinkClickedAnalyticsListeners } from "./Analytics";
import { setLinkClickedAnalyticsListeners } from "./Analytics";
import { loadBotDefence } from "../helpers/botDefence";

class HeaderOutdoor extends HTMLElement {
  constructor() {
    super();

    if (!isWebview()) {
      this.root = this.attachShadow({ mode: "open" });
      loadBotDefence();
    }
  }

  connectedCallback() {
    this.connectedCallbackRequired();

    if (isWebview()) {
      const outdoorHeaderElement = document.querySelector("arcteryx-outdoor-header");
      outdoorHeaderElement.style.display = "none";
      hideInvalidElementsInWebView();
      hideCookieConsent();

      // SWAT-8119 hide Wunderkind signup popup in mobile-app webviews on PDP pages
      if (isPDPPage()) {
        hideEmailSignupPopup();
      }
    } else {
      this.connectedCallbackUI();
      this.refreshJwtInterval = Auth.refreshJWT(this.root, this.market);
    }
  }

  disconnectedCallback() {
    clearInterval(this.refreshJwtInterval);
  }

  connectedCallbackRequired() {
    this.country = getCountry(this);
    this.language = getLanguage(this);
    this.fdp = this.hasAttribute("fdp") && this.getAttribute("fdp") === "true";
    this.disableSearchCss = this.hasAttribute("disableSearchCss") && this.getAttribute("disableSearchCss") === "true";
    this.season = this.hasAttribute("season") ? this.getAttribute("season") : SEASON_OUTDOOR;
    this.year = this.hasAttribute("year") ? this.getAttribute("year") : YEAR_OUTDOOR;
    this.market = "outdoor";

    // SWAT-8244 add language in html tag
    document.getElementsByTagName("html")[0].lang = getOneTrustLang(this.language);

    if (!this.fdp) {
      ArcWebSettings.init("OUTDOOR", this.season, this.year, this.language, this.country);
    }

    // Initialize configuration for ForgeRock
    Auth.init(this.country, this.language, DEPLOY_ENV);

    // Load Adobe Target if featue flag is on
    AdobeTargetPreScript.load();

    // Load Adobe Launch ADOBE_LAUNCH_URL is set in webpack.common.js
    if (!this.fdp && ADOBE_LAUNCH_URL && !disableAdobeLaunch()) {
      loadScript(isJacketFinderPage() ? ADOBE_LAUNCH_JACKET_FINDER_URL : ADOBE_LAUNCH_URL);
    }

    if (!this.fdp && (typeof ARCTERYX === "undefined" || typeof ARCTERYX.analytics === "undefined")) {
      loadScript(`${SCRIPTS_URL}/js/analytics.min.js`);
    }
  }

  connectedCallbackUI() {
    // Add header content to body
    const headerTemplate = document.createElement("template");
    const translations = require(`../../utils/translation-data_${this.language}.js`).data;
    headerTemplate.innerHTML = headerTemplateHtml(translations, this.country, this.language);
    this.root.appendChild(headerTemplate.content.cloneNode(true));

    buildHeadMetaItems();
    this.bindEvents(this.root);
    this.bindListeners(this.root);
    addEmailSignupLinkClickListeners(this.root, this.country);
    appendCountryLanguage(this.root, this.country, this.language);
    updateClassNames(this.root, ".OUTDOOR", this.country, this.language);
    isUserLoggedIn(this.root, ".OUTDOOR");
    updateCartCount(this.root, this.market, this.country);
    initializeLightbox(this.root);

    if (!this.fdp) {
      // FDP pages get this font loaded through js-helpers <GlobalStyles />
      injectURWFont();
    }
    hideElementInIframe(this.root, ".OUTDOOR");
    injectOfficeClosureMessage(CMS_URL, "OUTDOOR", this.country, this.language);
    injectWCAGStatement(translations["WCAG Statement"]);
    switchToCheckoutHeader(this.root);
    injectPromobarMessage(this.root, this.market, this.country, this.language, this.fdp);
  }

  createSearchContainer() {
    const searchContainerName = "DeadBirdSearchContainer";
    let searchContainer = document.getElementById(searchContainerName);
    if (!searchContainer) {
      searchContainer = document.createElement("div");
      searchContainer.id = searchContainerName;
      this.insertAdjacentElement("afterend", searchContainer);
    } else {
      searchContainer = document.getElementById(searchContainerName);
    }
    return searchContainer;
  }

  async loadSearch(el) {
    this.search = await import(/* webpackChunkName: "search-outdoor" */ "../../search/index.js");
    const searchIconEl = el.querySelector(".header-search");
    searchIconEl.style.opacity = 1;
    searchIconEl.addEventListener(
      "click",
      () => {
        this.openSearch(el);
      },
      true
    );
    document.addEventListener("search-closed", () => {
      toggleRegularPage(true);
    });

    if (window.location.search.match(/search=/)) {
      this.openSearch(el);
      return;
    }
    searchIconEl.click();
  }

  openSearch(el) {
    toggleRegularPage(false);
    const searchContainer = this.createSearchContainer();
    const searchIconEl = el.querySelector(".header-search");
    searchContainer.classList.toggle("open");
    const indexConstructor = {
      market: this.market,
      language: this.language,
      country: this.country,
    };
    this.search.InitSearch(el.searchIsOpen, searchContainer, indexConstructor);
    searchIconEl.style.removeProperty("opacity");
    el.firstChild.classList.toggle("search-mode");
    document.body.classList.toggle("search-mode");
  }

  bindEvents(el) {
    if (window.location.search.match(/search=/)) {
      this.loadSearch(el);
    }

    el.querySelector(".header-search").addEventListener(
      "click",
      () => {
        this.loadSearch(el);
      },
      { once: true }
    );

    el.querySelectorAll(".header .flag").forEach(el => {
      el.classList.add(`flag-${this.country.toUpperCase()}`);
      el.addEventListener("click", () => {
        const flagEv = new Event("ARCTERYXUICOMPONENTS.localeClick", {
          bubbles: true,
          cancelable: false,
        });
        flagEv.initEvent("ARCTERYXUICOMPONENTS.localeClick", true, true);
        this.dispatchEvent(flagEv);
      });
    });
  }

  bindListeners(el) {
    document.addEventListener("search-closed", () => {
      el.querySelector(".header-search").style.opacity = 1;
      document.body.classList.toggle("search-mode");
      el.firstChild.classList.toggle("search-mode");
      document.getElementById("DeadBirdSearchContainer").classList.toggle("open");
    });

    setLinkClickedAnalyticsListeners(el, ".sale__banner .header_content ul li a", "header");
    setNavigationLinkClickedAnalyticsListeners(el, "nav .sub-menu .container ul li a");
    setNavigationLinkClickedAnalyticsListeners(el, "#gift_guide");
    el.querySelectorAll("nav .sub-menu .container ul li a").forEach(navLink => {
      if (window.innerWidth >= 1300) {
        // Show feature image on link hover
        const featuredEl = this.getClosest(navLink, ".container").querySelector(".feature .content");
        navLink.addEventListener(
          "mouseover",
          () => {
            this.getAndSetFeatureImage(navLink);
            featuredEl.classList.add("hovered");
          },
          { passive: true }
        );
        navLink.addEventListener(
          "mouseleave",
          () => {
            featuredEl.classList.remove("hovered");
          },
          { passive: true }
        );
      }
    });

    el.querySelectorAll("nav .sub-menu").forEach(dropDown => {
      dropDown.addEventListener(
        "mouseover",
        () => {
          addHoverForNav(dropDown, el, "OUTDOOR");
          toggleHotjar();
        },
        { passive: true }
      );
      dropDown.addEventListener(
        "mouseleave",
        () => {
          removeAllHoverForNav(el, "OUTDOOR");
          toggleHotjar();
          if (dropDown.querySelectorAll(".shopmore .mobilefly li").length) {
            const navItemClassLists = Array.from(
              dropDown.querySelectorAll(".shopmore .mobilefly li:not(:first-child)")
            ).map(navItemEl => Array.from(navItemEl.classList));
            const navItemIndex = navItemClassLists.findIndex(navItemClassList =>
              navItemClassList.includes(`${this.country.toUpperCase()}-only`)
            );
            const navItems = dropDown.querySelectorAll(".shopmore .mobilefly li:not(:first-child)");
            const defaultFeature = navItems[navItemIndex < 0 ? 0 : navItemIndex].querySelector("a");
            setTimeout(() => {
              this.getAndSetFeatureImage(defaultFeature);
            }, 50);
          }
        },
        { passive: true }
      );
    });
    if (window.innerWidth < 769 && "ontouchstart" in document.documentElement) {
      el.querySelector("nav #men.head").addEventListener(
        "touchstart",
        e => {
          toggleHoverForNav(e.target, el, "OUTDOOR");
          removeHoverForNav(el.querySelector("nav #women.head"));
          toggleOverflowForNav(el);
          toggleHotjar();
        },
        { passive: true }
      );
      el.querySelector("nav #women.head").addEventListener(
        "touchstart",
        e => {
          toggleHoverForNav(e.target, el, "OUTDOOR");
          removeHoverForNav(el.querySelector("nav #men.head"));
          toggleOverflowForNav(el);
          toggleHotjar();
        },
        { passive: true }
      );
    } else {
      el.querySelectorAll("nav .head").forEach(dropDown => {
        dropDown.addEventListener("click", e => {
          if (e.target.id == "explore") {
            e.preventDefault();
          }
        });
        dropDown.addEventListener(
          "mouseover",
          () => {
            addHoverForNav(dropDown, el, "OUTDOOR");
            toggleHotjar();
          },
          { passive: true }
        );
        dropDown.addEventListener(
          "mouseleave",
          () => {
            removeAllHoverForNav(el, "OUTDOOR");
            toggleHotjar();
          },
          { passive: true }
        );
      });
    }
    if ("ontouchstart" in document.documentElement) {
      el.querySelectorAll("nav .head a, nav a.head").forEach(navLink => {
        navLink.addEventListener("touchstart", e => {
          e.preventDefault();
        });
      });
      el.querySelectorAll("nav .mobilefly li a").forEach(mobileLi => {
        mobileLi.addEventListener("touchstart", e => {
          e.preventDefault();
          trackClick(mobileLi);
          window.location.href = e.target.getAttribute("href");
        });
      });
    }
    // Set the default feature image

    el.querySelectorAll("ul.shopmore").forEach(single => {
      const navItemClassLists = Array.from(single.querySelectorAll(".mobilefly li:not(:first-child)")).map(navItemEl =>
        Array.from(navItemEl.classList)
      );
      const navItemIndex = navItemClassLists.findIndex(navItemClassList =>
        navItemClassList.includes(`${this.country.toUpperCase()}-only`)
      );
      const navItems = single.querySelectorAll(".mobilefly li:not(:first-child)");
      const defaultFeature = navItems[navItemIndex < 0 ? 0 : navItemIndex].querySelector("a");

      this.getAndSetFeatureImage(defaultFeature);
    });
    el.querySelectorAll("ul.explore").forEach(single => {
      this.getAndSetFeatureImage(single.querySelectorAll(".mobilefly li")[0].querySelector("a"));
    });
    const divOverlay = document.createElement("div");
    divOverlay.id = "content--overlay";
    divOverlay.classList.add("overlay-dark--full");
    divOverlay.style.display = "none";
    divOverlay.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
    document.body.appendChild(divOverlay);

    document.querySelector("#content--overlay").addEventListener("touchstart", () => {
      removeAllHoverForNav(el, "OUTDOOR");
    });

    window.addEventListener("cart-update", () => {
      updateCartCount(this.root, this.market, this.country);
    });

    this.importantMessageLightBox = listenForImportantMessage();
  }

  getClosest(elem, selector) {
    let newElem = elem;
    // Fallback for IE
    if (!Element.prototype.matches) {
      Element.prototype.matches = Element.prototype.msMatchesSelector;
    }
    for (; newElem && newElem !== document; newElem = newElem.parentNode) {
      if (newElem.matches(selector)) {
        return newElem;
      }
    }
    return null;
  }

  getAndSetFeatureImage(hoveredEl) {
    let featureImageBox, imageName, imageText;
    if (!hoveredEl.classList.contains("social-na")) {
      if (hoveredEl.parentNode.classList) {
        featureImageBox = this.getClosest(hoveredEl, ".container").querySelector(".feature .content");
      }
      let gender = "";
      if (hoveredEl.getAttribute("data-prefix") !== null) {
        gender = `${hoveredEl.getAttribute("data-prefix")}-`;
      }

      featureImageBox.href = hoveredEl.getAttribute("href");
      imageName = hoveredEl.parentNode.classList
        .toString()
        .replace("sectionhead", "")
        .replace("style-scope", "")
        .replace("arcteryx-outdoor-header", "")
        .replace("gift-card-countries-only", "")
        .trim();
      imageName = imageName.split(" ")[0];
      imageText = hoveredEl.textContent;

      if (hoveredEl.parentNode.classList.contains("rocksolid") && this.country != "US") {
        imageText = hoveredEl.parentNode.nextElementSibling.textContent;
        imageName = hoveredEl.parentNode.nextElementSibling.className;
        gender = `${hoveredEl.parentNode.nextElementSibling.querySelector("a").getAttribute("data-prefix")}-`;
      }
      const imageUrl = `https://images-dynamic-arcteryx.imgix.net/nav/feature/f23/${gender}${imageName}.jpg?auto=format`;
      setTimeout(() => {
        featureImageBox.style.backgroundImage = `url(${imageUrl})`;
        featureImageBox.querySelector("h2").textContent = imageText;
      }, 300);
    }
  }
}

export default HeaderOutdoor;
